import React, { useState, useEffect } from 'react';
import '../App.css';

function ScoringChecklist({ initialRequirements, clientChanges, requirements, submission, onScoreUpdate, onAIAnalyze, hideImage = false, isHistoricalGame = false, isHost = false, isLocked = false }) {
  // Support both old API (requirements) and new API (clientChanges)
  const scoringRequirements = clientChanges || requirements || [];
  
  const [scores, setScores] = useState(
    scoringRequirements.reduce((acc, req) => {
      const reqId = typeof req === 'string' ? req : req.id;
      acc[reqId] = submission.scores?.[reqId] || 0;
      return acc;
    }, {})
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Update scores when submission prop changes
  useEffect(() => {
    if (submission && submission.scores) {
      const newScores = scoringRequirements.reduce((acc, req) => {
        const reqId = typeof req === 'string' ? req : req.id;
        acc[reqId] = submission.scores?.[reqId] || 0;
        return acc;
      }, {});
      setScores(newScores);
    }
  }, [submission, scoringRequirements]);

  const handleScoreChange = (reqId, value, points = 1) => {
    // For bonus requirements, use the points value (4) when checked, 0 when unchecked
    const scoreValue = value ? points : 0;
    const newScores = { ...scores, [reqId]: scoreValue };
    setScores(newScores);
    onScoreUpdate(submission.id, newScores);
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((sum, score) => sum + (parseInt(score) || 0), 0);
  };

  const handleAIAnalysis = async () => {
    if (onAIAnalyze && !isAnalyzing) {
      setIsAnalyzing(true);
      try {
        const aiSuggestions = await onAIAnalyze(submission);
        // Merge AI suggestions with current scores
        const newScores = { ...scores };
        Object.keys(aiSuggestions).forEach(reqId => {
          if (aiSuggestions[reqId] !== undefined) {
            newScores[reqId] = aiSuggestions[reqId];
          }
        });
        setScores(newScores);
        onScoreUpdate(submission.id, newScores);
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  return (
    <div className="scoring-checklist">
      {!hideImage && submission.preview && (
        <div style={{ marginBottom: '1rem' }}>
          <img 
            src={submission.preview} 
            alt={`${submission.designerName}'s submission`}
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              borderRadius: '6px',
              border: '2px solid #e0e0e0'
            }}
          />
        </div>
      )}

      {/* Initial Requirements (read-only reference) */}
      {initialRequirements && initialRequirements.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            fontSize: '1.2rem', 
            color: '#ffffff', 
            marginBottom: '1.25rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }}>
            Initial Requirements
          </h4>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            color: '#ffffff'
          }}>
            {initialRequirements.map((req, index) => (
              <li 
                key={index} 
                style={{
                  padding: '0.125rem 0',
                  marginBottom: '0.125rem',
                  fontSize: '0.875rem', // 14px
                  lineHeight: '1.6',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}
              >
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.6)',
                  display: 'block',
                  flexShrink: 0
                }} />
                <span style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  flex: 1
                }}>
                  {req}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Client Changes (scored with checkboxes) */}
      {clientChanges && clientChanges.length > 0 && (
        <div>
          <h4 style={{ 
            fontSize: '1.2rem', 
            color: '#ffffff', 
            marginBottom: '1.25rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }}>
            Client Changes
          </h4>
          <div className="requirements-checklist">
            {clientChanges.map(req => {
              const points = req.points || 1;
              const isBonus = req.isBonus || false;
              return (
                <div 
                  key={req.id} 
                  style={{
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    background: isBonus 
                      ? 'rgba(255, 193, 7, 0.25)' 
                      : 'rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    borderLeft: isBonus 
                      ? '4px solid rgba(255, 193, 7, 0.7)' 
                      : '4px solid rgba(255, 255, 255, 0.4)',
                    border: isBonus
                      ? '1px solid rgba(255, 193, 7, 0.4)'
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    transition: 'background 0.2s'
                  }}
                >
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    cursor: isHost ? 'pointer' : 'default',
                    gap: '0.75rem',
                    width: '100%'
                  }}>
                    {!isHost ? (
                      // Player view: Show green checkmark if host marked it correct, nothing if not scored yet
                      scores[req.id] > 0 ? (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          marginRight: '0.75rem',
                          color: '#22c55e',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✓
                        </span>
                      ) : (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          marginRight: '0.75rem',
                          flexShrink: 0
                        }}>
                          {/* Empty space for alignment when not scored */}
                        </span>
                      )
                    ) : isHistoricalGame ? (
                      // Host view for historical games: Show checkmark or X
                      scores[req.id] > 0 ? (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          marginRight: '0.75rem',
                          color: '#22c55e',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✓
                        </span>
                      ) : (
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          marginRight: '0.75rem',
                          color: '#ef4444',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          ✕
                        </span>
                      )
                    ) : (
                      // Host view for active games: Show checkbox for scoring
                      <input
                        type="checkbox"
                        checked={scores[req.id] > 0}
                        onChange={(e) => handleScoreChange(req.id, e.target.checked, points)}
                        className="requirement-checkbox"
                        disabled={isLocked}
                        style={{
                          opacity: isLocked ? 0.5 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer'
                        }}
                      />
                    )}
                    <span style={{
                      flex: 1,
                      lineHeight: '1.7',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexWrap: 'wrap',
                      fontSize: '0.875rem', // 14px
                      fontWeight: 400
                    }}>
                      {req.text}
                      {isBonus && (
                        <span style={{
                          background: 'rgba(255, 193, 7, 0.5)',
                          color: '#FFC107',
                          padding: '0.35rem 0.65rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          border: '1px solid rgba(255, 193, 7, 0.6)',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                        }}>
                          +{points} pts
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback: Show requirements if clientChanges not provided (for backward compatibility) */}
      {!clientChanges && requirements && requirements.length > 0 && (
        <div className="requirements-checklist">
          {requirements.map(req => (
            <div 
              key={req.id} 
              className="requirement-item"
            >
              <label className="requirement-label">
                {!isHost ? (
                  // Player view: Show green checkmark if host marked it correct
                  scores[req.id] > 0 ? (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      marginRight: '0.75rem',
                      color: '#22c55e',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✓
                    </span>
                  ) : (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      marginRight: '0.75rem',
                      flexShrink: 0
                    }}>
                      {/* Empty space for alignment when not scored */}
                    </span>
                  )
                ) : isHistoricalGame ? (
                  // Host view for historical games: Show checkmark or X
                  scores[req.id] > 0 ? (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      marginRight: '0.75rem',
                      color: '#22c55e',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✓
                    </span>
                  ) : (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      marginRight: '0.75rem',
                      color: '#ef4444',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      ✕
                    </span>
                  )
                ) : (
                  // Host view for active games: Show checkbox for scoring
                  <input
                    type="checkbox"
                    checked={scores[req.id] > 0}
                    onChange={(e) => handleScoreChange(req.id, e.target.checked ? 1 : 0)}
                    className="requirement-checkbox"
                    disabled={isLocked}
                    style={{
                      opacity: isLocked ? 0.5 : 1,
                      cursor: isLocked ? 'not-allowed' : 'pointer'
                    }}
                  />
                )}
                <span className="requirement-text">{req.text}</span>
              </label>
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: 'rgba(215, 2, 246, 0.2)',
        border: '2px solid rgba(215, 2, 246, 0.5)',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '0.25rem' }}>
          Total Score
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#D702F6' }}>
          {calculateTotal()} pts
        </div>
      </div>
    </div>
  );
}

export default ScoringChecklist;
